import { useEffect, useRef, useState } from 'react';
import { penaltyCodeLabel } from '@flm9/i18n/labels';
import type { PenaltyCode, PenaltyViewRow } from '@flm9/sync';
import { api, isOffline, UnauthenticatedError } from '../lib/api';
import { errorText } from '../lib/errorText';
import { formatPenaltyPoints, type PenaltyValues } from '../lib/penalties';
import { addPenalty, removePenalty } from '../lib/sync';
import { PenaltySheet } from './PenaltySheet';

const UNDO_MS = 6_000;

/** "vom Server abgelehnt (HTTP 409)" for bare status codes, else the server's German reason. */
export function rejectionText(lastError: string | undefined): string {
  if (!lastError || /^http_\d+$/.test(lastError))
    return `vom Server abgelehnt${lastError ? ` (${lastError.replace('http_', 'HTTP ')})` : ''}`;
  return lastError;
}

/**
 * The shooter's penalties on the score screen: "Strafe" button (opens the
 * PenaltySheet), the list with per-item outbox state (amber dot = not sent,
 * rejection reason inline) and "Entfernen" with an undo toast. Read-only when
 * `editable` is false (round confirmed / DQ without recording).
 */
export function PenaltySection({
  assignmentId,
  shooterLabel,
  rows,
  total,
  values,
  editable,
  onChanged,
  onAuthExpired,
}: {
  assignmentId: string;
  shooterLabel: string;
  rows: readonly PenaltyViewRow[];
  total: number;
  values: PenaltyValues | undefined;
  editable: boolean;
  /** Refetch the server's penalties (after a direct, non-outbox write). */
  onChanged: () => void | Promise<void>;
  onAuthExpired: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [removed, setRemoved] = useState<{ code: PenaltyCode; note: string | null } | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);

  // A shooter change (or the round getting locked) drops the pending undo.
  useEffect(() => {
    setRemoved(null);
    setSheetOpen(false);
  }, [assignmentId]);
  useEffect(() => {
    if (!editable) {
      setRemoved(null);
      setSheetOpen(false);
    }
  }, [editable]);
  const removedRef = useRef(removed);
  removedRef.current = removed;
  useEffect(() => {
    if (!removed) return;
    const t = window.setTimeout(() => setRemoved(null), UNDO_MS);
    return () => window.clearTimeout(t);
  }, [removed]);

  if (!editable && rows.length === 0) return null;

  async function add(code: PenaltyCode, note: string | undefined) {
    try {
      await addPenalty({ roundAssignmentId: assignmentId, code, ...(note ? { note } : {}) });
      setSheetOpen(false);
    } catch (err) {
      console.error('addPenalty failed', err);
      alert('Strafe konnte nicht gespeichert werden. Bitte erneut versuchen.');
    }
  }

  async function remove(row: PenaltyViewRow) {
    if (removingKey) return;
    setRemovingKey(row.key);
    try {
      if (row.clientIdempotencyKey) {
        await removePenalty(assignmentId, row.clientIdempotencyKey);
      } else if (row.id) {
        // Penalty from before offline entry (no key): only directly, online.
        await api.removePenaltyById(assignmentId, row.id);
        await onChanged();
      } else return;
      setRemoved({ code: row.code, note: row.note });
    } catch (err) {
      console.error('removePenalty failed', err);
      if (err instanceof UnauthenticatedError) onAuthExpired();
      else if (isOffline(err))
        alert('Offline — diese ältere Strafe kann nur mit Verbindung entfernt werden.');
      else alert(`Entfernen fehlgeschlagen: ${errorText(err)}`);
    } finally {
      setRemovingKey(null);
    }
  }

  async function undo() {
    const r = removedRef.current;
    if (!r || undoBusy) return;
    setUndoBusy(true);
    try {
      await addPenalty({
        roundAssignmentId: assignmentId,
        code: r.code,
        ...(r.note ? { note: r.note } : {}),
      });
    } catch (err) {
      console.error('undo penalty remove failed', err);
      alert('Rückgängig fehlgeschlagen — bitte die Strafe neu vergeben.');
    } finally {
      setUndoBusy(false);
      setRemoved(null);
    }
  }

  return (
    <section aria-labelledby="penalties-title" className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 id="penalties-title" className="text-sm font-semibold text-zinc-300">
          Strafen
          {rows.length > 0 && (
            <span className="ml-2 tabular-nums text-orange-300">{formatPenaltyPoints(total)}</span>
          )}
        </h3>
        {editable && (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="min-h-12 shrink-0 rounded-lg border-2 border-orange-700 bg-orange-950/40 px-4 py-2 text-sm font-bold text-orange-100 hover:border-orange-500 active:bg-orange-900/60"
          >
            ⚑ Strafe
          </button>
        )}
      </div>
      {rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li
              key={row.key}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                row.status === 'failed'
                  ? 'border-amber-700 bg-amber-950/30'
                  : 'border-zinc-800 bg-zinc-900'
              }`}
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-zinc-100">
                  <span className="truncate">{penaltyCodeLabel(row.code)}</span>
                  {row.status === 'pending' && (
                    <>
                      <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-amber-400" />
                      <span className="sr-only"> noch nicht gesendet</span>
                    </>
                  )}
                </p>
                {row.note && <p className="truncate text-xs text-zinc-400">{row.note}</p>}
                {row.status === 'failed' && (
                  <p role="alert" className="text-xs text-amber-200">
                    ⚠️ {rejectionText(row.lastError)}
                  </p>
                )}
              </div>
              <span className="shrink-0 text-lg font-bold tabular-nums text-orange-300">
                {row.points != null ? formatPenaltyPoints(row.points) : '…'}
              </span>
              {editable && (
                <button
                  type="button"
                  onClick={() => void remove(row)}
                  disabled={removingKey != null}
                  aria-label={`${penaltyCodeLabel(row.code)} entfernen`}
                  className="min-h-12 min-w-12 shrink-0 rounded border border-zinc-700 px-3 text-sm text-zinc-300 hover:border-red-500 hover:text-red-300 disabled:opacity-50"
                >
                  Entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {sheetOpen && (
        <PenaltySheet
          shooterLabel={shooterLabel}
          values={values}
          onCancel={() => setSheetOpen(false)}
          onPick={add}
        />
      )}
      {removed && (
        <div
          role="status"
          className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 shadow-xl"
        >
          <span className="min-w-0 truncate">
            Strafe entfernt ({penaltyCodeLabel(removed.code)})
          </span>
          <button
            type="button"
            onClick={() => void undo()}
            disabled={undoBusy}
            className="min-h-12 shrink-0 rounded px-3 font-bold text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
          >
            Rückgängig
          </button>
        </div>
      )}
    </section>
  );
}
